import React, { useEffect, useState, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, X as XIcon } from "lucide-react";

/**
 * IntegrationHealthGuidedTour — interactive overlay that walks the user
 * through the live page step-by-step, highlighting one real DOM element
 * at a time with an explanatory tooltip.
 *
 * Beats a recorded video on three counts:
 *  1. Always in sync with the actual UI (a video would go stale).
 *  2. Highlights live data — coordinators see *their* current numbers
 *     while the tour explains what they mean.
 *  3. ~3 KB component vs. a multi-MB MP4.
 *
 * Each step references an existing `data-testid` selector. If a target
 * isn't on the page (e.g. error state), the tour gracefully skips it.
 */

const STEPS = [
  {
    target: '[data-testid="health-mode-banner"]',
    title: "1. Top banner — Integration mode",
    body: (
      <>
        <p>
          The big <b>PRODUCTION</b> / <b>PREVIEW</b> label is your at-a-glance
          status. The 6 pills next to it tell you which env vars are set.
        </p>
        <p className="mt-2">
          Hover any pill — a green one means that integration leg is wired and
          ready. A red pill means an env var is empty.
        </p>
      </>
    ),
    placement: "bottom",
  },
  {
    target: '[data-testid="last-seed-card"]',
    title: "2. Avida → us · seed-draft",
    body: (
      <>
        <p>
          Last time Avida sent us a coordinator-built visit assignment.
        </p>
        <p className="mt-2">
          If green and recent — Avida's outbound webhook is firing and our
          HMAC verification is accepting it. If gray — no traffic yet
          (normal post-deploy).
        </p>
      </>
    ),
    placement: "right",
  },
  {
    target: '[data-testid="last-completion-card"]',
    title: "3. Us → Avida · completion ACK",
    body: (
      <>
        <p>
          Last time we delivered a finished wizard's PDF bundle to Avida and
          Avida ACKd 200.
        </p>
        <p className="mt-2">
          Green here means the EHR has the documents. Stays gray until the
          first RN completes a wizard.
        </p>
      </>
    ),
    placement: "left",
  },
  {
    target: '[data-testid="last-outcome-card"]',
    title: "4. Us → Avida · pa-outcome ACK",
    body: (
      <>
        <p>
          Last time an admin recorded a PA approval/denial and we delivered
          the regenerated documents to Avida.
        </p>
      </>
    ),
    placement: "right",
  },
  {
    target: '[data-testid="last-failure-card"]',
    title: "5. Last failure",
    body: (
      <>
        <p>
          Most recent webhook failure across either leg. Click into the PA
          Outcomes inbox if red — coordinators can manually resend.
        </p>
        <p className="mt-2">
          Sentry has the full stack trace for engineering.
        </p>
      </>
    ),
    placement: "left",
  },
  {
    target: '[data-testid="counter-sync-failed"]',
    title: "6. Sync-failed counter",
    body: (
      <>
        <p>
          The most actionable counter on the page. Goes red-accent the moment
          it's <b>greater than 0</b>.
        </p>
        <p className="mt-2">
          Each one is a draft whose Avida webhook exhausted retries —
          coordinators must click <b>Resend</b> in /drafts/pa-outcomes once
          the upstream issue is fixed.
        </p>
      </>
    ),
    placement: "top",
  },
  {
    target: '[data-testid="counter-with-recipients"]',
    title: "7. With-recipients counter",
    body: (
      <>
        <p>
          How many agencies have at least one alert email set. If this is
          less than the <b>Agencies</b> tile to the left, the missing ones
          fall back to <code className="bg-gray-100 px-1 rounded">EMAIL_ALERT_FALLBACK_RECIPIENTS</code>.
        </p>
        <p className="mt-2">
          Set per-agency recipients via PA Outcomes inbox → <b>Recipients</b>.
        </p>
      </>
    ),
    placement: "top",
  },
  {
    target: '[data-testid="health-config-card"]',
    title: "8. Configuration",
    body: (
      <>
        <p>
          Read-only values from <code className="bg-gray-100 px-1 rounded">backend/.env</code>.
          Use this when something looks wrong — confirm the URL hasn't typo'd.
        </p>
        <p className="mt-2">
          <b>Loopback mode</b> = true means the Avida URL points back at this
          pod (preview demo). <b>Mock enabled</b> should be false in
          production.
        </p>
      </>
    ),
    placement: "top",
  },
  {
    target: '[data-testid="health-refresh-btn"]',
    title: "9. Refresh + auto-refresh",
    body: (
      <>
        <p>
          The page polls every 5s when auto-refresh is on. The backend
          caches for 5s server-side so polling doesn't hammer Mongo.
        </p>
        <p className="mt-2">
          Click <b>Refresh</b> to force-skip the cache.
        </p>
      </>
    ),
    placement: "bottom",
  },
];


function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

export default function IntegrationHealthGuidedTour({ open, onClose }) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const tooltipRef = useRef(null);

  const step = STEPS[index];

  const advance = useCallback((direction) => {
    setIndex((i) => clamp(i + direction, 0, STEPS.length - 1));
  }, []);

  // Update spotlight position whenever the step changes or window resizes.
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const el = document.querySelector(step.target);
      if (!el) {
        setRect(null);
        return;
      }
      // Scroll element into view if it's outside the viewport.
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Wait one tick so getBoundingClientRect picks up the new scroll
      // position before we measure it.
      setTimeout(() => {
        const r = el.getBoundingClientRect();
        setRect({
          top: r.top, left: r.left, width: r.width, height: r.height,
          bottom: r.bottom, right: r.right,
        });
      }, 350);
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, { passive: true });
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update);
    };
  }, [open, step.target]);

  // Keyboard navigation: ←/→ for prev/next, Esc to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "Enter") {
        e.preventDefault();
        if (index === STEPS.length - 1) {
          onClose();
        } else {
          advance(1);
        }
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        advance(-1);
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, advance, onClose]);

  if (!open) return null;

  // Compute tooltip position relative to the highlighted rect.
  const tooltipStyle = (() => {
    if (!rect) {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }
    const TOOLTIP_W = 360;
    const TOOLTIP_H = 240;
    const PAD = 16;
    const placement = step.placement;
    let top, left;
    if (placement === "right" && rect.right + TOOLTIP_W + PAD < window.innerWidth) {
      top = rect.top;
      left = rect.right + PAD;
    } else if (placement === "left" && rect.left - TOOLTIP_W - PAD > 0) {
      top = rect.top;
      left = rect.left - TOOLTIP_W - PAD;
    } else if (placement === "top" && rect.top > TOOLTIP_H + PAD) {
      top = rect.top - TOOLTIP_H - PAD;
      left = rect.left;
    } else {
      // default: bottom
      top = rect.bottom + PAD;
      left = rect.left;
    }
    top = clamp(top, PAD, window.innerHeight - TOOLTIP_H - PAD);
    left = clamp(left, PAD, window.innerWidth - TOOLTIP_W - PAD);
    return { top: top + "px", left: left + "px" };
  })();

  return (
    <div
      className="fixed inset-0 z-50 pointer-events-none"
      data-testid="health-tour-overlay"
    >
      {/* Spotlight cutout — draws 4 dim panels around the highlighted rect. */}
      {rect ? (
        <>
          <div
            className="fixed bg-black/60 transition-all duration-300 pointer-events-auto"
            style={{ top: 0, left: 0, right: 0, height: rect.top - 8 + "px" }}
            onClick={onClose}
          />
          <div
            className="fixed bg-black/60 transition-all duration-300 pointer-events-auto"
            style={{
              top: rect.top - 8 + "px",
              left: 0,
              width: rect.left - 8 + "px",
              height: rect.height + 16 + "px",
            }}
            onClick={onClose}
          />
          <div
            className="fixed bg-black/60 transition-all duration-300 pointer-events-auto"
            style={{
              top: rect.top - 8 + "px",
              left: rect.right + 8 + "px",
              right: 0,
              height: rect.height + 16 + "px",
            }}
            onClick={onClose}
          />
          <div
            className="fixed bg-black/60 transition-all duration-300 pointer-events-auto"
            style={{
              top: rect.bottom + 8 + "px",
              left: 0,
              right: 0,
              bottom: 0,
            }}
            onClick={onClose}
          />
          {/* Highlight ring around the target. */}
          <div
            className="fixed pointer-events-none rounded-xl ring-4 ring-[#9333EA] transition-all duration-300"
            style={{
              top: rect.top - 4 + "px",
              left: rect.left - 4 + "px",
              width: rect.width + 8 + "px",
              height: rect.height + 8 + "px",
              boxShadow: "0 0 0 9999px rgba(0,0,0,0)",
            }}
          />
        </>
      ) : (
        <div
          className="fixed inset-0 bg-black/60 pointer-events-auto"
          onClick={onClose}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed bg-white rounded-xl shadow-xl p-4 pointer-events-auto transition-all duration-300"
        style={{ ...tooltipStyle, width: "360px", maxWidth: "92vw" }}
        data-testid="health-tour-tooltip"
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#9333EA] font-bold">
              Tour · {index + 1} of {STEPS.length}
            </div>
            <h3 className="font-display text-[16px] font-bold text-[#0B1E3A] mt-0.5">
              {step.title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#3A5275] hover:text-[#0B1E3A] -mt-1 -mr-1 p-1 rounded-md"
            data-testid="health-tour-close"
            aria-label="Close tour"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="text-[13px] text-[#3A5275] leading-relaxed">
          {step.body}
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="flex gap-1">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`block h-1.5 rounded-full transition-all ${
                  i === index ? "w-6 bg-[#9333EA]" : "w-1.5 bg-[#E5E7EB]"
                }`}
                aria-label={`Step ${i + 1}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => advance(-1)}
              disabled={index === 0}
              className="px-2.5 py-1 rounded-md text-[12px] font-semibold text-[#3A5275] disabled:opacity-40 hover:bg-[#F3F4F6]"
              data-testid="health-tour-prev"
            >
              <ChevronLeft className="w-4 h-4 inline" />
            </button>
            {index === STEPS.length - 1 ? (
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1 rounded-md text-[12px] font-semibold bg-[#9333EA] text-white hover:bg-[#7E22CE]"
                data-testid="health-tour-finish"
              >
                Finish
              </button>
            ) : (
              <button
                type="button"
                onClick={() => advance(1)}
                className="px-3 py-1 rounded-md text-[12px] font-semibold bg-[#9333EA] text-white hover:bg-[#7E22CE]"
                data-testid="health-tour-next"
              >
                Next <ChevronRight className="w-4 h-4 inline" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
